import { Link } from "react-router-dom";
import { Mail, ArrowUpRight, Linkedin } from "lucide-react";

import { ASSETS, COMPANY } from "@/lib/company";
import { openCookieSettings } from "@/lib/privacy";

export default function Footer({ settings }) {
  const email = settings?.email || COMPANY.email;
  const linkedin = settings?.linkedin_url;
  const tagline = settings?.footer_tagline || COMPANY.tagline;
  const emprenovusLogo = settings?.emprenovus_logo_url || ASSETS.emprenovus;
  const companyName = settings?.company_name || COMPANY.name;

  return (
    <footer
      className="relative border-t border-white/6 bg-ink-black text-brand-white"
      data-testid="public-footer"
    >
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-20 pb-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8">
          <div className="lg:col-span-5 space-y-6">
            <Link to="/" aria-label="Minga Architecture" className="inline-block">
              <img
                src={ASSETS.logo}
                alt="Minga Architecture"
                className="h-16 md:h-20 w-auto object-contain"
              />
            </Link>
            <p className="body-text max-w-md">{tagline}</p>
            <p className="text-xs uppercase tracking-[0.24em] text-brand-gray">
              Seattle, WA · Quito, Ecuador
            </p>
          </div>

          <div className="lg:col-span-3 space-y-4">
            <p className="eyebrow-muted">[ Explore ]</p>
            <ul className="space-y-2.5">
              {[
                { label: "About Us", href: "/about" },
                { label: "Residential Design", href: "/residential-design" },
                { label: "BIM Production", href: "/bim-production" },
                { label: "Contact", href: "/contact" },
              ].map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    className="link-underline text-sm text-brand-white/85 hover:text-brand-yellow"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4 space-y-5">
            <p className="eyebrow-muted">[ Contact ]</p>
            <a
              href={`mailto:${email}`}
              className="group inline-flex items-center gap-3 text-brand-white hover:text-brand-yellow transition-colors"
              data-testid="footer-email"
            >
              <Mail className="h-4 w-4 text-brand-yellow" />
              <span className="text-base md:text-lg font-medium tracking-tight">
                {email}
              </span>
              <ArrowUpRight className="h-4 w-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
            </a>

            {linkedin ? (
              <a
                href={linkedin}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-brand-white/85 hover:text-brand-yellow transition-colors"
              >
                <Linkedin className="h-4 w-4" />
                LinkedIn
              </a>
            ) : null}

            <div className="pt-2">
              <Link to="/contact" className="btn-outline text-[11px] py-2.5">
                Start a Project
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-brand-gray text-center">
          <Link
            to="/privacy-policy"
            className="hover:text-brand-yellow transition-colors"
          >
            Privacy Policy
          </Link>
          <Link
            to="/cookie-policy"
            className="hover:text-brand-yellow transition-colors"
          >
            Cookie Policy
          </Link>
          <button
            type="button"
            onClick={openCookieSettings}
            className="hover:text-brand-yellow transition-colors"
          >
            Cookie Settings
          </button>
        </div>

        {/* Emprenovus footer branding (exact JSX per spec) */}
        <div className="mt-6 pt-6 border-t border-primary-foreground/15">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-xs text-brand-white text-center">
            <span>
              © {new Date().getFullYear()} {companyName}. All rights reserved.
            </span>

            <span className="hidden sm:inline opacity-40">|</span>

            <a
              href="https://www.emprenovus.com"
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-2 text-brand-white hover:text-brand-yellow transition-colors"
            >
              <img
                src={emprenovusLogo}
                className="h-6 w-6 object-contain opacity-95 group-hover:opacity-100"
                alt="Emprenovus"
              />
              <span>
                AI powered by <span className="font-medium">Emprenovus</span>
              </span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
